// Shared by the organizer payout setup form and the super-admin requests
// screen. Values match backend/server.py (PAYOUT_SETUP_* constants), which is
// the source of truth and re-validates everything.

export const INDIAN_STATES = [
  "Andaman and Nicobar Islands",
  "Andhra Pradesh",
  "Arunachal Pradesh",
  "Assam",
  "Bihar",
  "Chandigarh",
  "Chhattisgarh",
  "Dadra and Nagar Haveli and Daman and Diu",
  "Delhi",
  "Goa",
  "Gujarat",
  "Haryana",
  "Himachal Pradesh",
  "Jammu and Kashmir",
  "Jharkhand",
  "Karnataka",
  "Kerala",
  "Ladakh",
  "Lakshadweep",
  "Madhya Pradesh",
  "Maharashtra",
  "Manipur",
  "Meghalaya",
  "Mizoram",
  "Nagaland",
  "Odisha",
  "Puducherry",
  "Punjab",
  "Rajasthan",
  "Sikkim",
  "Tamil Nadu",
  "Telangana",
  "Tripura",
  "Uttar Pradesh",
  "Uttarakhand",
  "West Bengal",
];

export const ID_PROOF_KINDS = [
  { value: "aadhaar_masked", label: "Aadhaar card" },
  { value: "passport", label: "Passport" },
  { value: "voter_id", label: "Voter ID" },
  { value: "driving_licence", label: "Driving licence" },
];

export const STATUS_LABEL = {
  submitted: "Submitted",
  account_created: "Razorpay account created",
  under_review: "Under Razorpay review",
  activated: "Activated",
  connected: "Connected",
  needs_changes: "Needs changes",
  rejected: "Rejected",
};

export const STATUS_STYLE = {
  submitted: "bg-zinc-100 text-zinc-700 border-zinc-200",
  account_created: "bg-sky-50 text-sky-700 border-sky-200",
  under_review: "bg-sky-50 text-sky-700 border-sky-200",
  activated: "bg-emerald-50 text-emerald-700 border-emerald-200",
  connected: "bg-emerald-50 text-emerald-700 border-emerald-200",
  needs_changes: "bg-amber-50 text-amber-800 border-amber-200",
  rejected: "bg-red-50 text-red-700 border-red-200",
};

export const PAN_RE = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
export const IFSC_RE = /^[A-Z]{4}0[A-Z0-9]{6}$/;
export const PIN_RE = /^[1-9][0-9]{5}$/;
export const MOBILE_RE = /^[6-9][0-9]{9}$/;

export const MAX_DOCUMENT_BYTES = 2 * 1024 * 1024;
export const ACCEPTED_DOCUMENT_TYPES = ["image/jpeg", "image/png", "application/pdf"];
export const ACCEPT_ATTR =
  ".jpg,.jpeg,.png,.pdf,.heic,.heif,image/jpeg,image/png,image/heic,image/heif,application/pdf";

// Phone photos are usually 2-5 MB. Before uploading, any photo that is too
// big (or not JPG/PNG, e.g. HEIC) is redrawn at most MAX_IMAGE_EDGE pixels on
// its long side and saved as JPEG, which keeps an ID card sharp for Razorpay
// while landing well under the 2 MB limit. PDFs are sent as they are.
const MAX_IMAGE_EDGE = 2000;

function loadWithImgTag(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = (err) => {
      URL.revokeObjectURL(url);
      reject(err);
    };
    img.src = url;
  });
}

function loadImage(file) {
  if (typeof createImageBitmap === "function") {
    // imageOrientation keeps phone photos upright (EXIF rotation).
    return createImageBitmap(file, { imageOrientation: "from-image" }).catch(() =>
      loadWithImgTag(file)
    );
  }
  return loadWithImgTag(file);
}

function canvasToJpeg(canvas, quality) {
  return new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", quality));
}

export async function prepareDocumentFile(file) {
  const isPdf = file.type === "application/pdf" || /\.pdf$/i.test(file.name);
  if (isPdf) return file;
  const isImage = (file.type || "").startsWith("image/") || /\.(jpe?g|png|heic|heif)$/i.test(file.name);
  if (!isImage) return file;
  const isJpgOrPng = file.type === "image/jpeg" || file.type === "image/png";
  if (isJpgOrPng && file.size <= MAX_DOCUMENT_BYTES) return file;

  let source;
  try {
    source = await loadImage(file);
  } catch {
    return file; // The browser can't read it; the size/type checks will explain.
  }
  const width = source.width;
  const height = source.height;
  const scale = Math.min(1, MAX_IMAGE_EDGE / Math.max(width, height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(width * scale));
  canvas.height = Math.max(1, Math.round(height * scale));
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#ffffff"; // PNGs with transparency get a white background.
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(source, 0, 0, canvas.width, canvas.height);
  if (typeof source.close === "function") source.close();

  const baseName = (file.name || "photo").replace(/\.[^.]+$/, "") || "photo";
  for (const quality of [0.85, 0.75, 0.65, 0.5]) {
    const blob = await canvasToJpeg(canvas, quality);
    if (blob && blob.size <= MAX_DOCUMENT_BYTES) {
      return new File([blob], `${baseName}.jpg`, { type: "image/jpeg" });
    }
  }
  return file;
}

export function formatBytes(bytes) {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function formatDateTime(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
}

export function formatAge(seconds) {
  if (seconds === null || seconds === undefined) return "—";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${Math.max(1, minutes)}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ${minutes % 60}m`;
  const days = Math.floor(hours / 24);
  return `${days}d ${hours % 24}h`;
}

// India Post spells some states differently (or uses "&"); map them to ours.
const STATE_ALIASES = {
  pondicherry: "Puducherry",
  orissa: "Odisha",
  chattisgarh: "Chhattisgarh",
  uttaranchal: "Uttarakhand",
  "new delhi": "Delhi",
  "nct of delhi": "Delhi",
  "andaman and nicobar": "Andaman and Nicobar Islands",
  "dadra and nagar haveli": "Dadra and Nagar Haveli and Daman and Diu",
  "daman and diu": "Dadra and Nagar Haveli and Daman and Diu",
  "the dadra and nagar haveli and daman and diu": "Dadra and Nagar Haveli and Daman and Diu",
};

export function matchIndianState(name) {
  const key = (name || "").toLowerCase().replace(/&/g, "and").replace(/\s+/g, " ").trim();
  return INDIAN_STATES.find((state) => state.toLowerCase() === key) || STATE_ALIASES[key] || null;
}

// Free public PIN code directory (India Post data, no keys, called from the
// browser). Best-effort: returns null when the service can't be reached,
// { notFound: true } for an unknown PIN, else { city, state } where state is
// null if it couldn't be matched to our list.
export async function lookupPincode(pin) {
  try {
    const response = await fetch(`https://api.postalpincode.in/pincode/${encodeURIComponent(pin)}`);
    if (!response.ok) return null;
    const data = await response.json();
    const entry = Array.isArray(data) ? data[0] : null;
    if (!entry || entry.Status !== "Success" || !entry.PostOffice?.length) return { notFound: true };
    const office = entry.PostOffice[0];
    const city = office.District || office.Block || office.Name || "";
    // India Post still files Leh and Kargil under Jammu & Kashmir.
    const state = /^(leh|kargil)/i.test(city) ? "Ladakh" : matchIndianState(office.State);
    return { city, state };
  } catch {
    return null;
  }
}

// Free public IFSC directory run by Razorpay (no keys, CORS-enabled).
// Best-effort: returns null when the lookup fails for any reason.
export async function lookupIfsc(ifsc) {
  try {
    const response = await fetch(`https://ifsc.razorpay.com/${encodeURIComponent(ifsc)}`);
    if (response.status === 404) return { notFound: true };
    if (!response.ok) return null;
    const data = await response.json();
    return { bank: data.BANK || "", branch: data.BRANCH || "", city: data.CITY || "" };
  } catch {
    return null;
  }
}
